import React from 'react';
import { Link } from 'react-router-dom';
import { usePlants } from '../../hooks/usePlants';
import { useGrowingZone } from '../../hooks/useGrowingZone';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import PlantCard from '../PlantCard';
import { Loader2, Sprout } from 'lucide-react';
import type { Plant } from '../../types/plant';

export default function RecommendedPlants() {
  const { zone } = useGrowingZone();
  const { user } = useAuth();
  const { plants, loading, error } = usePlants(zone, null, null, null, true, 3); // Added limit parameter
  const [gardenPlantIds, setGardenPlantIds] = React.useState<Set<string>>(new Set());

  // Fetch user's garden plants
  React.useEffect(() => {
    async function fetchGardenPlants() {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('user_gardens')
          .select('plant_id')
          .eq('user_id', user.id);

        if (error) throw error;
        setGardenPlantIds(new Set(data.map(item => item.plant_id)));
      } catch (err) {
        console.error('Error fetching garden plants:', err);
      }
    }

    fetchGardenPlants();
  }, [user]);

  if (error) {
    return null;
  }

  return (
    <div className="w-full bg-white py-4 compatible-plants">
      <div className="container mx-auto px-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-3">
          <h2 className="text-lg font-bold">
            {zone ? `Compatible Plants for Zone ${zone}` : 'Compatible Plants'}
          </h2>
          <Link
            to="/plants"
            className="inline-flex items-center gap-1.5 text-primary-500 hover:text-primary-600 font-medium transition-colors text-sm lg:text-base"
          >
            <Sprout className="h-4 w-4" />
            <span>
              {zone ? `Explore All Zone ${zone} Plants` : 'Explore All Plants'}
            </span>
          </Link>
        </div>
        
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
          </div>
        ) : plants.length === 0 ? (
          <p className="text-gray-600">No plants found for your zone.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {plants.map((plant) => (
              <div key={plant.id} className="h-full">
                <PlantCard 
                  plant={plant}
                  isInGarden={gardenPlantIds.has(plant.id)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}